// The single source of truth for sim ↔ three-world ↔ screen mapping.
//
//   three.x =  sim.y      (rink width across the screen, ±15)
//   three.y =  height above the ice (0 = ice plane)
//   three.z = -sim.x      (rink length; home goal at +z, near the camera)
//
// Everything here is zero-alloc: `*Into` functions write into caller-owned
// outputs; the module-scope scratch Vector3 backs the projection helper.

import type { Vec2 } from "@arcade-hockey/sim";
import { type PerspectiveCamera, Vector3 } from "three/webgpu";

export type ScreenSize = { width: number; height: number };

const PROJECT_SCRATCH = new Vector3();

/** Sim position → three world position (writes `out`). */
export function simToWorldInto(out: Vector3, sim: Vec2, height = 0): void {
  out.set(sim.y, height, -sim.x);
}

/** Sim direction → three world direction on the ice plane (writes `out`). */
export function simDirToWorldInto(out: Vector3, dir: Vec2): void {
  out.set(dir.y, 0, -dir.x);
}

/**
 * Yaw (rotation.y) that points an object's local +z axis along the given
 * sim direction. Used for fins/shields whose geometry is authored facing +z.
 */
export function worldYawFromSimDir(dir: Vec2): number {
  // world dir = (dir.y, 0, -dir.x); rotation.y = atan2(x, z) for +z-forward.
  return Math.atan2(dir.y, -dir.x);
}

/**
 * Sim position (+ height) → screen pixels for the given camera. Used by the
 * DOM HUD anchoring (faceoff bar), the offscreen arrow, and audio panning.
 */
export function simToScreenInto(
  out: { x: number; y: number },
  sim: Vec2,
  height: number,
  camera: PerspectiveCamera,
  screen: ScreenSize,
): void {
  PROJECT_SCRATCH.set(sim.y, height, -sim.x).project(camera);
  out.x = (PROJECT_SCRATCH.x * 0.5 + 0.5) * screen.width;
  out.y = (-PROJECT_SCRATCH.y * 0.5 + 0.5) * screen.height;
}
